import React, { useState, useRef } from 'react';
import { View, StyleSheet, Alert, Image, Platform } from 'react-native';
import {
  Text,
  Button,
  Card,
  IconButton,
  useTheme,
  ActivityIndicator,
} from 'react-native-paper';
import {
  launchImageLibrary,
  launchCamera,
  ImagePickerResponse,
  MediaType,
} from 'react-native-image-picker';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { apiClient } from '@coffeeplexity/api-client';
import { logger } from '../../../utils/logger';

interface ImageUploadProps {
  onImageUploaded: (url: string) => void;
  onImageRemoved: () => void;
  currentImage?: string;
  maxSize?: number; // in MB
  className?: string;
}

export function ImageUpload({
  onImageUploaded,
  onImageRemoved,
  currentImage,
  maxSize = 5,
}: ImageUploadProps) {
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isWeb = Platform.OS === 'web';

  const compressImage = async (uri: string): Promise<string> => {
    try {
      // First, resize to max width of 1200px while maintaining aspect ratio
      const resized = await manipulateAsync(
        uri,
        [{ resize: { width: 1200 } }],
        { compress: 0.8, format: SaveFormat.JPEG }
      );

      // Check file size and compress further if needed
      const response = await fetch(resized.uri);
      const blob = await response.blob();

      if (blob.size > maxSize * 1024 * 1024) {
        // If still too large, compress more aggressively
        const moreCompressed = await manipulateAsync(
          resized.uri,
          [{ resize: { width: 800 } }],
          { compress: 0.6, format: SaveFormat.JPEG }
        );
        return moreCompressed.uri;
      }

      return resized.uri;
    } catch (error) {
      logger.error('Image compression failed:', error);
      throw new Error('Failed to compress image');
    }
  };

  const handleImageUpload = async (uri: string) => {
    setError(null);
    setIsUploading(true);

    try {
      // Compress the image
      const compressedUri = await compressImage(uri);

      // Convert URI to File object for React Native
      const fetchResponse = await fetch(compressedUri);
      const blob = await fetchResponse.blob();
      const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });

      // Upload to server
      const uploadResponse = await apiClient.uploadFile(file, 'image');
      onImageUploaded(uploadResponse.url);
    } catch (error) {
      logger.error('Upload error:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to upload image. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Web file upload handler
  const handleWebFileUpload = async (file: File) => {
    setError(null);
    setIsUploading(true);

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size
      if (file.size > maxSize * 1024 * 1024) {
        throw new Error(`File size must be less than ${maxSize}MB`);
      }

      // Create a data URL from the file for compression
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Compress the image
      const compressedUri = await compressImage(dataUrl);

      // Convert compressed URI back to File
      const fetchResponse = await fetch(compressedUri);
      const blob = await fetchResponse.blob();
      const compressedFile = new File([blob], 'image.jpg', { type: 'image/jpeg' });

      // Upload to server
      const uploadResponse = await apiClient.uploadFile(compressedFile, 'image');
      onImageUploaded(uploadResponse.url);
    } catch (error) {
      logger.error('Upload error:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to upload image. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleWebFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleWebFileUpload(file);
    }
    // Reset input so the same file can be selected again
    if (event.target) {
      event.target.value = '';
    }
  };

  const showImagePicker = () => {
    if (isWeb) {
      // On web, trigger the hidden file input
      fileInputRef.current?.click();
    } else {
      // On native, show the picker dialog
      Alert.alert('Select Image', 'Choose how you want to add an image', [
        { text: 'Camera', onPress: openCamera },
        { text: 'Photo Library', onPress: openImageLibrary },
        { text: 'Cancel', style: 'cancel' },
      ]);
    }
  };

  const openCamera = () => {
    launchCamera(
      {
        mediaType: 'photo' as MediaType,
        quality: 0.8,
        maxWidth: 1200,
        maxHeight: 1200,
      },
      handleImagePickerResponse
    );
  };

  const openImageLibrary = () => {
    launchImageLibrary(
      {
        mediaType: 'photo' as MediaType,
        quality: 0.8,
        maxWidth: 1200,
        maxHeight: 1200,
      },
      handleImagePickerResponse
    );
  };

  const handleImagePickerResponse = (response: ImagePickerResponse) => {
    if (response.didCancel || response.errorMessage) {
      return;
    }

    if (response.assets && response.assets[0]) {
      const asset = response.assets[0];
      if (asset.uri) {
        handleImageUpload(asset.uri);
      }
    }
  };

  const removeImage = () => {
    if (isWeb) {
      // On web, use window.confirm instead of Alert
      if (window.confirm('Are you sure you want to remove this image?')) {
        onImageRemoved();
        setError(null);
      }
    } else {
      Alert.alert('Remove Image', 'Are you sure you want to remove this image?', [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: () => {
            onImageRemoved();
            setError(null);
          },
        },
      ]);
    }
  };

  return (
    <View style={styles.container}>
      {/* Hidden file input for web */}
      {isWeb && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleWebFileChange}
          style={{ display: 'none' }}
        />
      )}

      {currentImage ? (
        <Card style={styles.imageCard}>
          <Image
            source={{ uri: currentImage }}
            style={styles.image}
            resizeMode='cover'
          />
          <View style={styles.imageOverlay}>
            <View style={styles.imageActions}>
              <Button
                mode='contained-tonal'
                onPress={showImagePicker}
                disabled={isUploading}
                compact
              >
                Replace
              </Button>
              <IconButton
                icon='delete'
                mode='contained-tonal'
                onPress={removeImage}
                disabled={isUploading}
                iconColor={theme.colors.error}
              />
            </View>
          </View>
        </Card>
      ) : (
        <Card
          style={[styles.uploadCard, { borderColor: theme.colors.outline }]}
        >
          <Card.Content style={styles.uploadContent}>
            {isUploading ? (
              <View style={styles.uploadingState}>
                <ActivityIndicator size='large' />
                <Text variant='bodyMedium' style={styles.uploadingText}>
                  Uploading image...
                </Text>
              </View>
            ) : (
              <View style={styles.uploadPrompt}>
                <IconButton
                  icon='camera'
                  size={48}
                  iconColor={theme.colors.onSurfaceVariant}
                />
                <Text
                  variant='bodyMedium'
                  style={[
                    styles.uploadText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Add an image to your recipe
                </Text>
                <Button
                  mode='outlined'
                  onPress={showImagePicker}
                  style={styles.uploadButton}
                >
                  Choose Image
                </Button>
                <Text
                  variant='bodySmall'
                  style={[
                    styles.uploadHint,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Supports JPEG, PNG up to {maxSize}MB
                </Text>
              </View>
            )}
          </Card.Content>
        </Card>
      )}

      {error && (
        <Card
          style={[
            styles.errorCard,
            { backgroundColor: theme.colors.errorContainer },
          ]}
        >
          <Card.Content>
            <Text
              variant='bodySmall'
              style={{ color: theme.colors.onErrorContainer }}
            >
              {error}
            </Text>
          </Card.Content>
        </Card>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  imageCard: {
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: 200,
  },
  imageOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.3)',
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0,
  },
  imageActions: {
    flexDirection: 'row',
    gap: 12,
  },
  uploadCard: {
    borderWidth: 2,
    borderStyle: 'dashed',
  },
  uploadContent: {
    paddingVertical: 32,
  },
  uploadingState: {
    alignItems: 'center',
  },
  uploadingText: {
    marginTop: 12,
  },
  uploadPrompt: {
    alignItems: 'center',
  },
  uploadText: {
    marginBottom: 16,
    textAlign: 'center',
  },
  uploadButton: {
    marginBottom: 8,
  },
  uploadHint: {
    textAlign: 'center',
  },
  errorCard: {
    marginTop: 8,
  },
});
